import { createSlice, PayloadAction, createSelector } from '@reduxjs/toolkit';
import {
  MediaType,
  VoiceContactStatus,
  DigitalChannelStatus,
  DigitalChannelType,
  contactButtons,
  VoiceAuthenticationStatus,
  AgentAssistStatus,
} from '@nice-ccf/core-sdk';
import { Contact } from './model/contact';
import { ContactPayload } from './model/contact-payload';
import { TranscriptMessage, INITIAL_TRANSCRIPT } from './model/transcript';
import { RtaStatusPayload } from './model/rtaStatus-payload';
import { ContactHistoryFilterData } from './model/contach-history-search-filter';
import { DigitalChannelMessage } from '../ccf-interaction-workspace/ccf-interaction-body/ccf-digital-interaction-module-type';
import { AppSpace } from '../../enums/app-space-tabs';
import { ConsumerAppSpace } from '../../enums/consumer-app-space-tabs';

export const UNIFIED_INBOX_KEY = 'inbox';

export interface CustomerHistory {
  id?: string;
  contactId: string;
  contactRequest: string;
  contactTrackingId: string;
  contactName: string;
  contactStatus: string;
  digitalChannel: string;
  isOutbound: boolean;
  updatedDate: string;
}

const customerHistory: CustomerHistory[] = [
  {
    id: '0',
    contactId: '253-895-8956',
    contactRequest: 'Airline Customer Service',
    contactTrackingId: '423-INCL-0246',
    contactName: 'Agent Smith',
    contactStatus: 'New',
    digitalChannel: 'digital',
    isOutbound: false,
    updatedDate: '13/10/2021',
  },
  {
    id: '1',
    contactId: 'DavisBRLiam342@sunsyst.com',
    contactRequest: 'Question about my luggage',
    contactTrackingId: '78-EMAIL-5S98',
    contactName: 'Agent Kapoor',
    contactStatus: 'Open',
    digitalChannel: 'email',
    isOutbound: true,
    updatedDate: '13/10/2021',
  },
  {
    id: '2',
    contactId: '253-895-8956',
    contactRequest: 'Customer Service',
    contactTrackingId: '897-OTVO-7455',
    contactName: 'Agent Tiwari',
    contactStatus: 'Pending',
    digitalChannel: 'voice',
    isOutbound: false,
    updatedDate: '13/10/2021',
  },
  {
    id: '3',
    contactId: '253-895-8956',
    contactRequest: 'Cell Phone Number Edit',
    contactTrackingId: '',
    contactName: 'Agent Pablo Fransico',
    contactStatus: 'Resolved',
    digitalChannel: 'voice',
    isOutbound: true,
    updatedDate: '13/10/2021',
  },
  {
    id: '4',
    contactId: 'DavisBRLiam342@sunsyst.com',
    contactRequest: 'Tickets Purchased',
    contactTrackingId: '456-EMAIL-6245',
    contactName: 'Agent Winston',
    contactStatus: 'Escalated',
    digitalChannel: 'email',
    isOutbound: true,
    updatedDate: '13/10/2021',
  },
  {
    id: '5',
    contactId: 'DavisBRLiam342@sunsyst.com',
    contactRequest: 'Channel added',
    contactTrackingId: '',
    contactName: 'Agent Tiwari',
    contactStatus: 'Closed',
    digitalChannel: 'email',
    isOutbound: true,
    updatedDate: '13/10/2021',
  },
  {
    id: '6',
    contactId: '360-253-8956',
    contactRequest: 'Customer added',
    contactTrackingId: '',
    contactName: 'Agent Dhaksan',
    contactStatus: 'Closed',
    digitalChannel: 'contactprofile',
    isOutbound: true,
    updatedDate: '13/10/2021',
  },
];

export interface InboxState {
  contacts: Contact[];
  lastContacts: Contact[];
  isInboxCollapsed: boolean;
  callType: 'p2p' | 'conference';
  appSpaceSelectedTab: {
    index: number;
    tab: string;
  };
  consumerAppSpaceSelectedTab: {
    index: number;
    tab: string;
  };
  isEmailWithApproval: boolean;
  isCoBrowsingSessionStarted: boolean;
  isChatJsonFileUploaded: boolean;
  isFileEndReached: boolean;
  showEnlightenTab: boolean;
  createNewCommitment: boolean;
  personalConnectionCount: number;
  isHungUp: boolean;
  isRejected: boolean;
  isCallAccepted: boolean;
  workingOBContacts: Contact[];
  rejectedOBContacts: Contact[];
  isEmailResponseLoadedInEditor: boolean;
  isCopilotEmailResponseGenerated: boolean;
  generatedEmailResponse: string[] | null | undefined;
  initialTranscript: TranscriptMessage[]; // Default voice call transcript, a contact's own transcriptData overrides it
}
export const initialInboxState: InboxState = {
  initialTranscript: INITIAL_TRANSCRIPT,
  contacts: [],
  lastContacts: [],
  isInboxCollapsed: false,
  callType: 'p2p',
  appSpaceSelectedTab: {
    index: 0,
    tab: AppSpace.CUSTOMERCARD,
    // tab: ConsumerAppSpace.OVERVIEW,
  },
  consumerAppSpaceSelectedTab: {
    index: 0,
    tab: ConsumerAppSpace.OVERVIEW,
  },
  isEmailWithApproval: false,
  isCoBrowsingSessionStarted: false,
  isChatJsonFileUploaded: true,
  isFileEndReached: false,
  showEnlightenTab: true, //feature toggle for Enlighten Tab
  createNewCommitment: false, //Create New Commitment flag
  personalConnectionCount: 0,
  isHungUp: false,
  isRejected: false,
  isCallAccepted: false,
  workingOBContacts: [
    {
      id: '1',
      active: true,
      isOutboundCall: false,
      isWorkingOB: true,
      isDocked: true,
      customerChatReply: '',
      customer: {
        accountNumber: '123456789',
        ssn: '6584845884',
        firstName: 'Alex',
        lastName: 'Smith',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8956',
        emailId: 'AlexSmith@sunsyst.com',
        twitterId: '@alexsmith',
        filteredHistoryCount: 7,
        customerHistory: customerHistory,
      },
      messageType: '253-895-8956',
      queueName: '',
      autoSummary: 'Off',
      media: MediaType.VOICE,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.hold,
        contactButtons.mask,
        contactButtons.mute,
        contactButtons.keypad,
        contactButtons.transfer,
        contactButtons.record,
        //contactButtons.commit,
        contactButtons.hungup,
      ],
    },
    {
      id: '2',
      active: true,
      isOutboundCall: false,
      isWorkingOB: true,
      isDocked: true,
      customerChatReply: '',
      customer: {
        accountNumber: '787685964',
        ssn: '443435566',
        firstName: 'John',
        lastName: 'Wills',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8956',
        emailId: 'JohnWills@sunsyst.com',
        twitterId: '@johnwills',
        filteredHistoryCount: 7,
        customerHistory: customerHistory,
      },
      messageType: '253-895-8956',
      queueName: '',
      autoSummary: 'Off',
      media: MediaType.VOICE,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.hold,
        contactButtons.mask,
        contactButtons.mute,
        contactButtons.keypad,
        contactButtons.transfer,
        contactButtons.record,
        //contactButtons.commit,
        contactButtons.hungup,
      ],
    },
    {
      id: '3',
      active: true,
      isOutboundCall: false,
      isWorkingOB: true,
      isDocked: true,
      customerChatReply: '',
      customer: {
        accountNumber: '98989656',
        ssn: '121324566',
        firstName: 'Steve',
        lastName: 'Cross',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8956',
        emailId: 'SteveCross@sunsyst.com',
        twitterId: '@stevecross',
        filteredHistoryCount: 7,
        customerHistory: customerHistory,
      },
      messageType: '253-895-8956',
      queueName: '',
      autoSummary: 'Off',
      media: MediaType.VOICE,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.hold,
        contactButtons.mask,
        contactButtons.mute,
        contactButtons.keypad,
        contactButtons.transfer,
        contactButtons.record,
        //contactButtons.commit,
        contactButtons.hungup,
      ],
    },
    {
      id: '4',
      active: true,
      isOutboundCall: false,
      isWorkingOB: true,
      isDocked: true,
      customerChatReply: '',
      customer: {
        accountNumber: '7746545',
        ssn: '543524323',
        firstName: 'David',
        lastName: 'Lee',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8956',
        emailId: 'DavidLee@sunsyst.com',
        twitterId: '@davidlee',
        filteredHistoryCount: 7,
        customerHistory: customerHistory,
      },
      messageType: '253-895-8956',
      queueName: '',
      autoSummary: 'Off',
      media: MediaType.VOICE,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.hold,
        contactButtons.mask,
        contactButtons.mute,
        contactButtons.keypad,
        contactButtons.transfer,
        contactButtons.record,
        //contactButtons.commit,
        contactButtons.hungup,
      ],
    },
    {
      id: '5',
      active: true,
      isOutboundCall: false,
      isWorkingOB: true,
      isDocked: true,
      customerChatReply: '',
      customer: {
        accountNumber: '6574675768',
        ssn: '322435666',
        firstName: 'Jack',
        lastName: 'Wood',
        customerPrimaryAddress: {
          address1: '1234 Super Long Name Ct.',
          address2: 'Auburn,',
          city: 'CA',
          zip: '95602',
          country: 'USA',
        },
        phoneNo: '253-895-8956',
        emailId: 'JackWood@sunsyst.com',
        twitterId: '@jackwood',
        filteredHistoryCount: 7,
        customerHistory: customerHistory,
      },
      messageType: '253-895-8956',
      queueName: '',
      autoSummary: 'Off',
      media: MediaType.VOICE,
      contactStatus: VoiceContactStatus.RINGING,
      rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
      agentAssistStatus: AgentAssistStatus.NEGATIVE,
      contactButtons: [
        contactButtons.hold,
        contactButtons.mask,
        contactButtons.mute,
        contactButtons.keypad,
        contactButtons.transfer,
        contactButtons.record,
        //contactButtons.commit,
        contactButtons.hungup,
      ],
    },
  ],
  rejectedOBContacts: [],
  isCopilotEmailResponseGenerated: false,
  isEmailResponseLoadedInEditor: false,
  generatedEmailResponse: [],
};

export const inboxSlice = createSlice({
  name: UNIFIED_INBOX_KEY,
  initialState: initialInboxState,
  reducers: {
    /**
     * Function to update call type
     * @param state - InboxState
     * @param action - tabindex
     * @example dispatch(updateCallType(`{ index : 0 }`))
     * @returns
     */
    updateCallType(state, action) {
      return {
        ...state,
        callType: action.payload,
        appSpaceSelectedTab: {
          ...state.appSpaceSelectedTab,
          index: 0,
        },
      };
    },
    /**
     * Function updates the app space tab index
     * @param state - InboxState
     * @param action - tab index
     * @returns -  updated app space tab label as 'ContactCard'
     *  @example - dispatch(updateAppSpaceTab(`{ index : 0 }`))
     */
    updateAppSpaceTabIndex(state, action) {
      return {
        ...state,
        appSpaceSelectedTab: {
          ...state.appSpaceSelectedTab,
          index: action.payload.index,
        },
      };
    },

    /**
     * Function updates the consumer app space tab index
     * @param state - InboxState
     * @param action - tab index
     * @returns -  updated app space tab label as 'ContactCard'
     *  @example - dispatch(updateConsumerAppSpaceTabIndex(`{ index : 0 }`))
     */
    updateConsumerAppSpaceTabIndex(state, action) {
      return {
        ...state,
        consumerAppSpaceSelectedTab: {
          ...state.consumerAppSpaceSelectedTab,
          index: action.payload.index,
        },
      };
    },
    /**
     * Function updates the app space tab label
     * @param state - InboxState
     * @param action - tab name
     * @returns - updated app space tab label as 'ContactCard'
     * @example - dispatch(updateAppSpaceTab(`{ tab : AppSpace.QUICK_REPLY }`))
     */
    updateAppSpaceTab(state, action) {
      return {
        ...state,
        appSpaceSelectedTab: {
          ...state.appSpaceSelectedTab,
          tab: action.payload.tab,
        },
      };
    },

    /**
     * Function updates the consumer app space tab label
     * @param state - InboxState
     * @param action - tab name
     * @returns - updated app space tab label as 'ContactCard'
     * @example - dispatch(updateConsumerAppSpaceTab(`{ tab : AppSpace.QUICK_REPLY }`))
     */
    updateConsumerAppSpaceTab(state, action) {
      return {
        ...state,
        consumerAppSpaceSelectedTab: {
          ...state.consumerAppSpaceSelectedTab,
          tab: action.payload.tab,
        },
      };
    },

    /**
     *
     * @param state -
     * @param action -
     * @example
     * @returns
     */
    updateActiveContact(
      state,
      action: PayloadAction<{ id: string; updates: any }>
    ) {
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.id === action.payload.id
            ? { ...contact, ...action.payload.updates }
            : contact
        ),
      };
    },

    /**
     * Function add item to rejectedOBContacts
     * @param state - InboxState
     * @param action - item to added
     * @returns - updated rejectedOBContacts
     * @example - dispatch(addToRejectedOBContacts([]))
     */
    addToRejectedOBContacts(state, action) {
      return {
        ...state,
        rejectedOBContacts: action.payload,
      };
    },

    // /**
    //  * Function updates the interaction contact transfer status to 'true'
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns updated voice interaction status as 'active'
    //  * @example dispatch(setContactTransferStatus(ContactPayload))
    //  */
    // setContactTransferStatus(state, action: PayloadAction<any>) {
    //   const { id } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? { ...contact, setUpForTransfer: true }
    //         : { ...contact, setUpForTransfer: false }
    //     ),
    //   };
    // },

    // /**
    //  * Function updates the voice interaction contact status to 'Active'
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns updated voice interaction status as 'active'
    //  * @example dispatch(setContactElevationStatus(ContactPayload))
    //  */
    // setContactElevationStatus(state, action: PayloadAction<any>) {
    //   const { id } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? { ...contact, isElevated: true }
    //         : { ...contact, isElevated: false }
    //     ),
    //   };
    // },
    /**
    //  * Function updates the voice interaction contact status to 'Active'
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns updated voice interaction status as 'active'
    //  * @example dispatch(acceptACDCall(ContactPayload))
    //  */
    // acceptACDCall(state, action: PayloadAction<ContactPayload>) {
    //   const { id } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? { ...contact, contactStatus: VoiceContactStatus.ACTIVE }
    //         : contact
    //     ),
    //   };
    // },
    /**
    //  * Function updates Digital interaction to 'open'
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns updated digital interaction status as 'open'
    //  * @example - dispatch(acceptDigitalContact(ContactPayload))
    //  */
    // acceptDigitalContact(state, action: PayloadAction<ContactPayload>) {
    //   const { id } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? { ...contact, contactStatus: DigitalChannelStatus.OPEN }
    //         : contact
    //     ),
    //   };
    // },

    // /**
    //  * Function updates Digital interaction to 'open'
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns updated digital interaction status as 'open'
    //  * @example - dispatch(acceptDigitalContact(ContactPayload))
    //  */
    // setRtigStateForContact(state, action: PayloadAction<any>) {
    //   //const { id } = action.payload.id;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === action.payload.id
    //         ? { ...contact, agentAssist: action.payload.rtigState }
    //         : contact
    //     ),
    //   };
    // },
    /**
    //  * Function updates contact status
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns update contact status
    //  * @example - dispatch(updateCallStatus(ContactPayload))
    //  */
    // updateContactElevationDetails(state, action: PayloadAction<any>) {
    //   const { id, elevationDetails } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? {
    //             ...contact,
    //             elevationDetails: elevationDetails,
    //           }
    //         : contact
    //     ),
    //   };
    // },
    /**
    //  * Function updates contact status
    //  * @param state -InboxState
    //  * @param action -PayloadAction<ContactPayload>
    //  * @returns update contact status
    //  * @example - dispatch(updateCallStatus(ContactPayload))
    //  */
    // updateCallStatus(state, action: PayloadAction<ContactPayload>) {
    //   const { id, contactStatus } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? {
    //             ...contact,
    //             contactStatus: contactStatus
    //               ? contactStatus
    //               : contact.contactStatus,
    //           }
    //         : contact
    //     ),
    //   };
    // },
    // /**
    //  * Function to update contact status for RTA
    //  * @param state - InboxState
    //  * @param action - PayloadAction<RtaStatusPayload>
    //  * @example - dispatch(updateContactStatus(RtaStatusPayload))
    //  * @returns
    //  */
    // updateContactStatus(state, action: PayloadAction<RtaStatusPayload>) {
    //   const { id, rtaStatus } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id ? { ...contact, rtaStatus: rtaStatus } : contact
    //     ),
    //   };
    // },
    /**
    //  * Function to update contact outBoundCallStatus
    //  * @param state - InboxState
    //  * @param action - PayloadAction<RtaStatusPayload>
    //  * @example - dispatch(updateContactoutBoundCallinitiated(Payload))
    //  * @returns
    //  */
    // updateContactoutBoundCallinitiated(state, action: PayloadAction<any>) {
    //   const { id, outBoundCallStatus } = action.payload;
    //   return {
    //     ...state,
    //     contacts: state.contacts.map((contact) =>
    //       contact.id === id
    //         ? { ...contact, outBoundInitiatedForVoiceMail: outBoundCallStatus }
    //         : contact
    //     ),
    //   };
    // },
    /**
     * Function removes the voice call from state
     * @param state -InboxState
     * @param action -PayloadAction<string>
     * @returns - removes voice interaction from store
     * @example - dispatch(rejectACDCall(string))
     */
    rejectACDCall(state, action: PayloadAction<string>) {
      return {
        ...state,
        contacts: state.contacts.filter(
          (details) => details.id !== action.payload
        ),
      };
    },
    /**
     * Function removes digital contact from state
     * @param state -InboxState
     * @param action -PayloadAction<string>
     * @returns - removes digital interaction from store
     * @example - diaptch(rejectDigitalContact(string))
     */
    rejectDigitalContact(state, action: PayloadAction<string>) {
      return {
        ...state,
        contacts: state.contacts.filter(
          (details) => details.id !== action.payload
        ),
      };
    },
    /**
     * Inserts new interactions from simulator
     * @param state -InboxState
     * @param action -PayloadAction<Contact>
     * @returns - inserts interaction in store from braodcast listener
     * @example - dipatch(useSimulatorToInsertContact(Contact))
     */
    useSimulatorToInsertContact(state, action: PayloadAction<Contact>) {
      let currentContact: Contact[] = [...state.contacts];
      const contactInInbox = currentContact.find(
        (contact) => contact.id === action.payload.id
      );
      const haveContactInInbox = contactInInbox ? true : false;

      if (haveContactInInbox && action.payload.media === MediaType.VOICE) {
        return state;
      } else if (haveContactInInbox && action.payload.channel === 'Chat') {
        return {
          ...state,
          contacts: currentContact.map((contact) =>
            contact.id === action.payload.id
              ? {
                  ...contact,
                  customerChatReply: action.payload.customerChatReply,
                }
              : { ...contact }
          ),
          isInboxCollapsed: false,
          callType: 'p2p',
        };
      }
      if (currentContact.length > 0) {
        currentContact = currentContact.map((contactEle: any) => {
          return {
            ...contactEle,
            contactHistoryList: action.payload.contactHistoryList,
          };
        });
      }
      //new call contact will be added to top of list and new digit contact will be added to bottom of list
      if (action.payload.media === MediaType.VOICE) {
        currentContact.unshift(action.payload);
        if (!('isDocked' in currentContact[0])) {
          currentContact[0]['isDocked'] = true;
        }
      } else {
        currentContact.push(action.payload);
      }
      const contactHistory = action.payload?.customer?.customerHistory || [];
      const inboundHistoryCount = inboundHistory(contactHistory);
      const outboundHistoryCount = outboundHistory(contactHistory);
      const internalHistoryCount = internalHistory(contactHistory);
      // action.payload.customer.filteredHistory =
      //   action.payload?.customer.customerHistory || [];
      // action.payload.customer.filteredHistoryCount =
      //   action.payload?.customer.filteredHistory?.length || 0;
      // action.payload.customer.totalHistoryCount =
      //   action.payload?.customer.customerHistory?.length;
      // action.payload.customer.ibHistoryCount = inboundHistoryCount?.length;
      // action.payload.customer.obHistoryCount = outboundHistoryCount?.length;
      // action.payload.customer.otherHistoryCount = internalHistoryCount?.length;

      return {
        ...state,
        contacts: currentContact,
        lastContacts: currentContact,
        isInboxCollapsed: false,
        callType: 'p2p',
      };
    },
    /**
     * Function to update digital contact to elevate voice controls
     * @param state - InboxState
     * @param action - PayloadAction<RtaStatusPayload>
     * @example - dispatch(updateDigitalContactToElevateVoiceControl(Payload))
     * @returns
     */
    updateDigitalContactToElevateVoiceControl(
      state,
      action: PayloadAction<any>
    ) {
      const { id } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.id === id
            ? {
                ...contact,
                messageType: 'Customer Srv.',
                contactStatus: 'active',
                rtaStatus: VoiceAuthenticationStatus.AUTHENTICATED,
                isDocked: true,
                contactButtons: [
                  contactButtons.hold,
                  contactButtons.mask,
                  contactButtons.mute,
                  contactButtons.keypad,
                  contactButtons.transfer,
                  contactButtons.record,
                ],
              }
            : contact
        ),
      };
    },
    /**
     * Updates the selected contact in assignemnt box
     * @param state  -InboxState
     * @param action -PayloadAction<Contact>
     * @example - dispatch(setSelectedContact(Contact))
     */
    setSelectedContact(state, action: PayloadAction<Contact>) {
      const contact = { ...action.payload };
      const customer = { ...contact.customer };
      const contactHistory = customer?.customerHistory;
      const inboundHistoryCount = inboundHistory(contactHistory);
      const outboundHistoryCount = outboundHistory(contactHistory);
      const internalHistoryCount = internalHistory(contactHistory);
      customer.filteredHistory = [...contactHistory];
      customer.filteredHistoryCount = customer?.filteredHistory?.length || 0;
      customer.totalHistoryCount = customer?.customerHistory?.length;
      customer.ibHistoryCount = inboundHistoryCount?.length;
      customer.obHistoryCount = outboundHistoryCount?.length;
      customer.otherHistoryCount = internalHistoryCount?.length;
      if (MediaType.VOICE !== action.payload.media) {
        return {
          ...state,
          contacts: state.contacts.map((contact) =>
            contact.id === action.payload.id
              ? {
                  ...contact,
                  isDocked: true,
                  active: true,
                  customer: customer,
                  lastVisited: new Date(),
                }
              : { ...contact, active: false }
          ),
        };
      } else {
        return {
          ...state,
          contacts: state.contacts.map((contact) =>
            contact.id === action.payload.id
              ? { ...contact, active: true, lastVisited: new Date() }
              : { ...contact, active: false }
          ),
        };
      }
    },
    /**
     * Function sets docked status for a contact in assignment box
     * @param state -InboxState
     * @param action -PayloadAction<ContactPayload>
     * @example - dispatch(setIsDocked(ContactPayload))
     */
    setIsDocked(state, action: PayloadAction<ContactPayload>) {
      const { id, isDocked } = action.payload;
      return {
        ...state,
        isInboxCollapsed: false,
        contacts: state.contacts.map((contact) =>
          contact.id === id ? { ...contact, isDocked: isDocked } : contact
        ),
      };
    },
    /**
     * Function sets assignmentbox collapsed/expanded state
     * @param state -InboxState
     * @param action -PayloadAction<boolean>
     * @example - dispatch(updateInboxCollapsed(booleanValue))
     */
    updateInboxCollapsed(state, action: PayloadAction<boolean>) {
      let haveCallInInbox = false;
      for (let i = 0; i < state.contacts.length; i++) {
        if (
          state.contacts[i]['contactStatus'] === VoiceContactStatus.RINGING ||
          state.contacts[i]['contactStatus'] === DigitalChannelStatus.NEW
        ) {
          //cannot collapse assignment panel since accept reject buttons are showing up
          haveCallInInbox = true;
          break;
        }
      }
      if (haveCallInInbox) {
        return state;
      } else {
        return {
          ...state,
          isInboxCollapsed: action.payload,
        };
      }
    },
    /**
     * Used to contact history as per inbox contact
     * @param state - InboxState
     * @param action - PayloadAction<ContactHistoryFilterData>
     * @example - `dispatch(loadContactHistoryData(ContactHistoryFilterData))`
     */
    loadContactHistoryData(
      state,
      action: PayloadAction<ContactHistoryFilterData>
    ) {
      const data = action.payload;
      const contact = { ...action.payload.activeContact };
      const customer = { ...contact.customer };
      const historyData = [...contact.customer.customerHistory];
      const filteredHistory = getContactHistoryFilterAndSearchData(
        historyData,
        data.filterByCategory,
        data.searchByInput
      );
      customer.filteredHistoryCount = filteredHistory?.length || 0;
      const slicedHistoryData =
        filteredHistory?.slice(data.start, data.next) || [];
      customer.filteredHistory = [...slicedHistoryData];
      contact.customer = customer;
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.id === action.payload.activeContact.id
            ? { ...contact, active: true, customer: customer }
            : { ...contact, active: false }
        ),
      };
    },
    /**
     * Used to update contact interaction
     * @param state - InboxState
     * @param action - PayloadAction<any>
     * @example dispatch(updateContactInteraction(`{id: '1234', interaction: 'SMS')}`))
     * @returns
     */
    updateContactInteraction(state, action: PayloadAction<any>) {
      const { id, interaction } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          return contact.id === id
            ? {
                ...contact,
                interaction: {
                  ...contact.interaction,
                  [interaction.id]: { ...interaction },
                },
                activeInteraction: interaction.id,
              }
            : contact;
        }),
      };
    },
    /**
     * Used to resolve dispotion for any interaction once it is completed
     * @param state - InboxState
     * @param action -PayloadAction<any>
     * @example dispatch(resolveDispositionForInteraction(`{id: '1234'}`))
     * @returns
     */
    resolveDispositionForInteraction(state, action: PayloadAction<any>) {
      const { id } = action.payload;
      const activeInteraction = state.contacts.find(
        (contact: Contact) => contact.id === id
      );
      if (activeInteraction?.interaction) {
        const updatedInteraction = {
          ...activeInteraction?.interaction[
            activeInteraction.activeInteraction as string
          ],
          isDispositionCompleted: true,
        };
        return {
          ...state,
          contacts: state.contacts.map((contact: Contact) =>
            contact.id === id
              ? {
                  ...contact,
                  interaction: {
                    ...contact.interaction,
                    [activeInteraction?.activeInteraction as string]:
                      updatedInteraction,
                  },
                }
              : contact
          ),
        };
      }
      return {
        ...state,
      };
    },
    /**
     * Function used to update active interaction
     * @param state - InboxState
     * @param action - PayloadAction<any>
     * @example dispatch(updateActiveInteraction(`{id: '1234', interactionId: 'SMS'}`));
     * @returns
     */
    updateActiveInteraction(state, action: PayloadAction<any>) {
      const { id, interactionId } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          return contact.id === id
            ? { ...contact, activeInteraction: interactionId }
            : contact;
        }),
      };
    },

    /**
     * Function used to update contact intents
     * @param state - InboxState
     * @param action - PayloadAction<any>
     * @example dispatch(updateContactIntents(`{id: '1234', intentsSet: []}`));
     * @returns
     */
    updateContactIntents(state, action: PayloadAction<any>) {
      const { id, intentsSet } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          return contact.id === id
            ? { ...contact, intents: intentsSet }
            : contact;
        }),
      };
    },

    updatePropertyOfAgentReply: (state, action) => {
      const { interactionId, replyMessage, deleteTweetId, actionType } =
        action.payload;
      /**
       * Function for filter agent reply
       * @param interaction - any
       * @example filterAgentReply()
       * @returns
       */
      const filterAgentReply = (interaction: any) => {
        return interaction
          ? actionType !== 'InitialCancel'
            ? {
                ...interaction[interactionId],

                agentReply: deleteTweetId
                  ? interaction[interactionId].agentReply.map(
                      (msg: DigitalChannelMessage) =>
                        msg.messageId === deleteTweetId
                          ? actionType === 'Save'
                            ? {
                                ...msg,
                                isEditNote: false,
                                messageContent: {
                                  ...msg.messageContent,
                                  payload: { text: replyMessage },
                                },
                              }
                            : actionType === 'Edit'
                            ? { ...msg, isEditNote: true }
                            : { ...msg, isEditNote: false }
                          : msg
                    )
                  : [...interaction[interactionId].agentReply],
              }
            : {
                ...interaction[interactionId],
                agentReply: interaction[interactionId].agentReply.filter(
                  (msg: DigitalChannelMessage) =>
                    msg.messageId !== deleteTweetId
                ),
              }
          : {};
      };

      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          return contact.active
            ? {
                ...contact,
                interaction: {
                  ...contact.interaction,
                  [interactionId]: filterAgentReply(contact.interaction),
                },
              }
            : contact;
        }),
      };
    },

    /**
     * function used to update twitter agent reply
     * @param state - InboxState
     * @param action - PayloadAction<any>
     * @example dispatch(updateTwitterAgentReply(`{interactionId: 1234, replyMessage}`))
     * @returns
     */
    updateTwitterAgentReply(state, action: PayloadAction<any>) {
      const { interactionId, replyMessage, deleteTweetId } = action.payload;
      /**
       * Function for filter agent reply
       * @param interaction - any
       * @example filterAgentReply()
       * @returns
       */
      const filterAgentReply = (interaction: any) => {
        return interaction
          ? {
              ...interaction[interactionId],
              agentReply: deleteTweetId
                ? interaction[interactionId].agentReply.filter(
                    (msg: DigitalChannelMessage) =>
                      msg.messageId !== deleteTweetId
                  )
                : [...interaction[interactionId].agentReply, replyMessage],
            }
          : {};
      };
      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          return contact.active
            ? {
                ...contact,
                interaction: {
                  ...contact.interaction,
                  [interactionId]: filterAgentReply(contact.interaction),
                },
              }
            : contact;
        }),
      };
    },
    /**
     * Function used to update customer channel information
     * @param state - InboxState
     * @param action - PayloadAction<any>
     * @example dispatch(updateCustomerChnnelInformation());
     * @returns
     */
    updateCustomerChnnelInformation(
      state,
      action: PayloadAction<{ id: any; field: string; value: string }>
    ) {
      return {
        ...state,
        contacts: state.contacts.map((contact) => {
          return contact.id === action.payload.id
            ? {
                ...contact,
                customer: {
                  ...contact.customer,
                  [action.payload.field]: action.payload.value,
                },
              }
            : contact;
        }),
      };
    },

    /**
     * Function to update does email sent with approval or not
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns favourite status
     * @example dispatch(updateEmailWithApproval(true));
     */
    updateEmailWithApproval(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isEmailWithApproval: action.payload,
      };
    },

    /**
     * Function to update if copilot email response is generated
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns if email response is generated
     * @example dispatch(setIsCopilotEmailResponseGenerated(true));
     */
    setIsEmailResponseLoadedInEditor(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isEmailResponseLoadedInEditor: action.payload,
      };
    },

    /**
     * Function to update if copilot email response is generated
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns if email response is generated
     * @example dispatch(setIsCopilotEmailResponseGenerated(true));
     */
    setIsCopilotEmailResponseGenerated(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCopilotEmailResponseGenerated: action.payload,
      };
    },
    /**
     * Sets the generated email response.
     * @param state - InboxState
     * @param action - PayloadAction<string>
     * @returns Updated state with the generated email response.
     * @example dispatch(setGeneratedEmailResponse('Email response'));
     */
    setGeneratedEmailResponse(state, action: PayloadAction<any[]>) {
      return {
        ...state,
        generatedEmailResponse: action.payload,
      };
    },

    /**
     * Function to check if co browsing session is initiated or not
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns Co browsing initiation status
     * @example dispatch(setCoBrowsingSessionStarted(true));
     */
    setCoBrowsingSessionStarted(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCoBrowsingSessionStarted: action.payload,
      };
    },

    /**
     * Function to check if json file is uploaded or not for chat
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns chat file upload status
     * @example dispatch(setJsonFileUploadStatus(true));
     */
    setJsonFileUploadStatus(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isChatJsonFileUploaded: action.payload,
      };
    },

    /**
     * Function to check if json file is ended or not for chat
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns chat file end reached
     * @example dispatch(setIsChatEnded(true));
     */
    setIsChatEnded(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isFileEndReached: action.payload,
      };
    },

    /**
     * Function to Create New Commitment
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It returns chat file upload status
     * @example dispatch(setNewCommitment(true));
     */
    setNewCommitment(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        createNewCommitment: action.payload,
      };
    },

    /**
     * Function to replace the default voice call transcript
     * @param state - InboxState
     * @param action - list of transcript messages
     * @example dispatch(setInitialTranscript(INITIAL_TRANSCRIPT));
     */
    setInitialTranscript(state, action: PayloadAction<TranscriptMessage[]>) {
      return {
        ...state,
        initialTranscript: action.payload,
      };
    },
    /**
     * Function to check Personal Connection count
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It maintains Personal Connection Count
     * @example dispatch(setPersonalConnectionCount(1));
     */
    setPersonalConnectionCount(state, action: PayloadAction<number>) {
      return {
        ...state,
        personalConnectionCount: action.payload,
      };
    },

    /**
     * Function to check call Rejected status
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It check if the voice call is Rejected or not
     * @example dispatch(setIsRejected(true));
     */
    setIsRejected(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isRejected: action.payload,
      };
    },

    /**
     * Function to check call hungup status
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It check if the voice call is hungup or not
     * @example dispatch(setIsHungUp(true));
     */
    setIsHungUp(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isHungUp: action.payload,
      };
    },

    /**
     * Function to check call accepted status
     * @param state - AgentState
     * @param action  - PayloadAction<any>
     * @returns It check if the voice call is accepted or not
     * @example dispatch(setIsCallAccepted(true));
     */
    setIsCallAccepted(state, action: PayloadAction<boolean>) {
      return {
        ...state,
        isCallAccepted: action.payload,
      };
    },

    /**
     * Function for inbox sorting
     * @param state -InboxState
     * @param action -PayloadAction<sortBy>
     * @returns updated active contacts by Date
     * @example - dispatch(setInboxSorting(sortBy))
     */
    setInboxSorting(state, action: PayloadAction<any>) {
      const { sortBy, sortByOrder } = action.payload;
      /**
       * @example sortContactsByDate()
       */
      const sortContactsByDate = (a: any, b: any) => {
        return sortByOrder === 'desc'
          ? b.getTime() - a.getTime() // sort by descending order
          : a.getTime() - b.getTime(); // sort by ascending order
      };

      return {
        ...state,
        contacts:
          sortBy === 'Created Date'
            ? state.contacts.slice().sort(
                (objA: any, objB: any) =>
                  sortContactsByDate(objA.dateCreated, objB.dateCreated) //sort contacts by Date Created
              )
            : state.contacts.slice().sort(
                (objA: any, objB: any) =>
                  sortContactsByDate(objA.lastVisited, objB.lastVisited) //sort contacts by Last Visited
              ),
      };
    },
    /**
     * Used to save the state of audio recording (inprogress/stopped)
     * @param state - file upload state
     * @param action - action payload
     *  @example - setAudioRecordingState(true);
     */
    setAudioRecordingState(state, action: PayloadAction<any>) {
      const { id, isRecordingInprogress } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.id === id
            ? { ...contact, isRecordingInprogress: isRecordingInprogress }
            : contact
        ),
      };
    },
    /**
     * Used to update the attachments state
     * @param state - file upload state
     * @param action - reducer action
     * @example -
     * ```
     * dispatch(updateAttachments({
     * [contactId]: parsedList,
     * }))
     * ```
     */
    updateAttachments(
      state,
      action: PayloadAction<{ id: string; attachments: any[] }>
    ) {
      const { id, attachments } = action.payload;
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.id === id
            ? {
                ...contact,
                attachments:
                  contact?.attachments && contact?.attachments?.length > 0
                    ? (contact.attachments as any[]).concat(attachments)
                    : attachments,
              }
            : contact
        ),
      };
    },
    /**
     * Used to remove the attachment based on the attachment id received
     * @param state - file upload state
     * @param action - reducer action
     * @example -
     * ```
     * dispatch(removeAttachment(123456));
     * ```
     */
    removeAttachment(state, action: PayloadAction<string>) {
      return {
        ...state,
        contacts: state.contacts.map((contact) =>
          contact.active
            ? {
                ...contact,
                attachments: contact?.attachments?.filter(
                  (item) => item.id !== action.payload
                ),
              }
            : contact
        ),
      };
    },
  },
});

export const inboxReducer = inboxSlice.reducer;
export const {
  updateCallType,
  updateAppSpaceTabIndex,
  updateConsumerAppSpaceTabIndex,
  rejectACDCall,
  useSimulatorToInsertContact,
  setSelectedContact,
  setIsDocked,
  updateInboxCollapsed,
  loadContactHistoryData,
  rejectDigitalContact,
  updateAppSpaceTab,
  updateConsumerAppSpaceTab,
  updateContactInteraction,
  updateActiveInteraction,
  updateTwitterAgentReply,
  updatePropertyOfAgentReply,
  resolveDispositionForInteraction,
  updateCustomerChnnelInformation,
  updateDigitalContactToElevateVoiceControl,
  updateEmailWithApproval,
  setCoBrowsingSessionStarted,
  setJsonFileUploadStatus,
  setInboxSorting,
  setNewCommitment,
  setPersonalConnectionCount,
  setIsHungUp,
  setIsRejected,
  setIsCallAccepted,
  addToRejectedOBContacts,
  setIsEmailResponseLoadedInEditor,
  setIsCopilotEmailResponseGenerated,
  updateContactIntents,
  setIsChatEnded,
  updateActiveContact,
  setGeneratedEmailResponse,
  setAudioRecordingState,
  updateAttachments,
  removeAttachment,
  setInitialTranscript,
} = inboxSlice.actions;

/**
 * Get the contact history by filter category and search input
 * @param history - CustomerHistory
 * @param filterByCategory - selected category like 'all', 'inbound' and etc
 * @param searchByInput - agent entered input in search box
 * @example - `dispatch(getContactHistoryFilterAndSearchData(historyData,filterByCategory, searchByInput))`
 */
const getContactHistoryFilterAndSearchData = (
  history: CustomerHistory[],
  filterByCategory = 'all',
  searchByInput = ''
) => {
  const filteredHistory = historyFilterByCategory(history, filterByCategory);
  const filteredHistoryBySearch = contactHistorySearch(
    filteredHistory,
    searchByInput
  );
  const filteredData = [...filteredHistoryBySearch];
  return filteredData;
};

/**
 * Get the contact history by search input
 * @param history -  CustomerHistory[]
 * @param search - string
 * @example `dispatch(contactHistorySearch(history, search))`
 */
const contactHistorySearch = (history: CustomerHistory[], search = '') => {
  if (search) {
    return history.filter((history: CustomerHistory) => {
      let historyData;
      if (
        history.contactName
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) > -1 ||
        history.contactRequest
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) > -1 ||
        history.contactTrackingId
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) > -1 ||
        history.contactId
          .toString()
          .toLowerCase()
          .indexOf(search.toLowerCase()) > -1
      ) {
        historyData = history;
      }
      return historyData;
    });
  } else {
    return history;
  }
};

/* Get the contact history by filter category */
/**
 * Get the contact history by filter category
 * @param history - CustomerHistory[]
 * @param category - selected category like 'all', 'inbound' and etc
 * @example -  `dispatch(historyFilterByCategory(history, filterByCategory))`
 */
const historyFilterByCategory = (
  history: CustomerHistory[],
  category = 'all'
) => {
  if (history) {
    switch (category) {
      case 'inbound':
        return inboundHistory(history);
      case 'outbound':
        return outboundHistory(history);
      case 'other':
        return internalHistory(history);
      default:
        return history;
    }
  }
  return history;
};
/**
 * Function provides inbox state
 * @param rootState - InboxState
 * @example const inboxState = getInboxState(state)
 */
const getInboxState = (rootState: { inbox: InboxState }) => {
  return rootState[UNIFIED_INBOX_KEY];
};

export const getContacts = createSelector(getInboxState, (state) => {
  return state.contacts;
});

export const getLastContacts = createSelector(getInboxState, (state) => {
  return state.lastContacts;
});

/* get assignments count */
export const getContactsCount = createSelector(getContacts, (state) => {
  return state?.length;
});
export const selectInboxCollapsedState = createSelector(
  getInboxState,
  (state) => state.isInboxCollapsed
);
export const activeContactSelector = createSelector(getInboxState, (state) =>
  state.contacts.find((x) => x.active)
);

export const getInitialTranscript = createSelector(
  getInboxState,
  (state) => state.initialTranscript
);

/** Transcript to show for the active contact: its own transcriptData, else the default initialTranscript */
export const getActiveContactTranscript = createSelector(
  activeContactSelector,
  getInitialTranscript,
  (contact, initialTranscript) => {
    const transcriptData = contact?.transcriptData;
    return Array.isArray(transcriptData) ? transcriptData : initialTranscript;
  }
);

export const contactHistoryData = createSelector(
  activeContactSelector,
  (state) => state?.customer.customerHistory
);

export const contactHistoryToShow = createSelector(
  activeContactSelector,
  (state) => state?.customer.filteredHistory
);

export const getContactOutBoundCallStatus = createSelector(
  activeContactSelector,
  (state) => state?.outBoundInitiatedForVoiceMail
);

export const selectCallType = createSelector(
  getInboxState,
  (state) => state.callType
);
export const selectAppSpaceActiveIndex = createSelector(
  getInboxState,
  (state) => state.appSpaceSelectedTab.index
);

export const selectConsumerAppSpaceActiveIndex = createSelector(
  getInboxState,
  (state) => state.consumerAppSpaceSelectedTab.index
);

/**
 * filtering inbound channel history
 * @param contactHistory - CustomerHistory[]
 * @example - `dispatch(inboundHistory(contactHistory))`
 */
export const inboundHistory = (contactHistory: CustomerHistory[]) => {
  const icData = contactHistory?.filter((item: CustomerHistory) => {
    let inboundData;
    if (
      !item.isOutbound &&
      item.contactStatus !== 'edited' &&
      item.contactStatus !== 'added'
    ) {
      inboundData = item;
    }
    return inboundData;
  });

  return icData;
};

/**
 * filtering outbound channel history
 * @param contactHistory - CustomerHistory[]
 * @example - `dispatch(outboundHistory(contactHistory))`
 */
export const outboundHistory = (contactHistory: CustomerHistory[]) => {
  const obData = contactHistory?.filter((item: CustomerHistory) => {
    let outboundData;
    if (
      item.isOutbound &&
      item.contactStatus !== 'edited' &&
      item.contactStatus !== 'added'
    ) {
      outboundData = item;
    }
    return outboundData;
  });
  return obData;
};

/**
 * filtering internal channel history which any added or edited data
 * @param contactHistory - CustomerHistory[]
 * @example - `dispatch(internalHistory(contactHistory))`
 */
export const internalHistory = (contactHistory: CustomerHistory[]) => {
  const internalData = contactHistory?.filter((item: CustomerHistory) => {
    let internalRecord;
    if (item.contactStatus === 'edited' || item.contactStatus === 'added') {
      internalRecord = item;
    }
    return internalRecord;
  });
  return internalData;
};

export const selectAppSpaceActiveTab = createSelector(
  getInboxState,
  (state) => state.appSpaceSelectedTab.tab
);

export const selectConsumerAppSpaceActiveTab = createSelector(
  getInboxState,
  (state) => state.consumerAppSpaceSelectedTab.tab
);

export const selectEmailWithApprovalState = createSelector(
  getInboxState,
  (state) => state.isEmailWithApproval
);
export const getIsCopilotEmailResponseGenerated = createSelector(
  getInboxState,
  (state) => state.isCopilotEmailResponseGenerated
);
export const getIsEmailResponseLoadedInEditor = createSelector(
  getInboxState,
  (state) => state.isEmailResponseLoadedInEditor
);
export const getGeneratedEmailResponse = createSelector(
  getInboxState,
  (state) => state.generatedEmailResponse
);
export const getCoBrowsingSessionStarted = createSelector(
  getInboxState,
  (state) => state.isCoBrowsingSessionStarted
);
export const getJsonFileUploadStatus = createSelector(
  getInboxState,
  (state) => state.isChatJsonFileUploaded
);

export const getIsFileEnded = createSelector(
  getInboxState,
  (state) => state.isFileEndReached
);

export const getNewCommitment = createSelector(
  getInboxState,
  (state) => state.createNewCommitment
);
export const getPersonalConnectionCount = createSelector(
  getInboxState,
  (state) => state.personalConnectionCount
);
export const getRejectedOBContacts = createSelector(
  getInboxState,
  (state) => state.rejectedOBContacts
);

export const getIsHungUp = createSelector(
  getInboxState,
  (state) => state.isHungUp
);
export const getIsRejected = createSelector(
  getInboxState,
  (state) => state.isRejected
);
export const getIsCallAccepted = createSelector(
  getInboxState,
  (state) => state.isCallAccepted
);
export const getEnlightenTabFeatureToggle = createSelector(
  getInboxState,
  (state) => state.showEnlightenTab
);

export const getInteractionTabs = createSelector(
  activeContactSelector,
  (state) => state?.interaction
);

export const getActiveInteractionID = createSelector(
  activeContactSelector,
  (state) => state?.activeInteraction
);

export const getActiveContactID = createSelector(
  activeContactSelector,
  (state) => state?.id
);

export const getContactElevationStatus = createSelector(
  activeContactSelector,
  (state) => state?.isElevated
);

export const getActiveInteraction = createSelector(
  activeContactSelector,
  (state) =>
    state?.interaction && state?.interaction[state?.activeInteraction as string]
);

export const getContactElevationDetails = createSelector(
  activeContactSelector,
  (state) => state?.elevationDetails
);

export const getContactBotTranscriptSelector = createSelector(
  activeContactSelector,
  (state) => state?.botTranscript
);

export const getActiveInteractionType = createSelector(
  activeContactSelector,
  (state) =>
    state?.interaction
      ? mapDigitalInteractionPlatform(
          state?.interaction[state?.activeInteraction || '']
            ?.realExternalPlatformId
        )
      : ''
);

export const getWorkingOBContacts = createSelector(getInboxState, (state) => {
  return state.workingOBContacts;
});

// eslint-disable-next-line @nice-cxone/ccf/required-tsdoc
export const getCustomerChatReplyJsonData = (caseId: any) =>
  createSelector(getInboxState, (state) => {
    return state?.contacts[caseId]?.customerChatReplyJsonData ?? [];
  });

/**
 * used to map digital interaction platform
 * @param platformId - string
 * @example mapDigitalInteractionPlatform('Twitter')
 * @returns
 */
export const mapDigitalInteractionPlatform = (platformId: string) => {
  const platformData: { [key: string]: string } = {
    tw: DigitalChannelType.TWITTER,
    chat: DigitalChannelType.CHAT,
    email: DigitalChannelType.EMAIL,
    fb: DigitalChannelType.FACEBOOK,
    ig: DigitalChannelType.INSTAGRAM,
    li: DigitalChannelType.LINKEDIN,
    sms: DigitalChannelType.SMS,
    ChatWithBotTranscript: DigitalChannelType.CHATWITHBOTRANSCRIPT,
    VideoCall: DigitalChannelType.VIDEOCALL,
    reimbursementClaim: 'reimbursementClaim',
  };
  return platformData[platformId];
};
export const getAttachments = createSelector(
  activeContactSelector,
  (state) => state?.attachments
);

export const getRecordingStatus = createSelector(
  activeContactSelector,
  (state) => state?.isRecordingInprogress
);
